import { useEffect, useState } from 'react';
import { X, Check, AlertTriangle, AlertCircle, Info, CheckCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';

type Notification = {
  id: number;
  type: string;
  severity: 'info' | 'warn' | 'critical';
  title: string;
  message: string;
  context: Record<string, unknown> | null;
  seller_order_id: string | null;
  created_at: string;
  read_at: string | null;
};

const SEVERITY_ICON: Record<string, JSX.Element> = {
  critical: <AlertCircle className="w-4 h-4 text-red-600" />,
  warn: <AlertTriangle className="w-4 h-4 text-amber-600" />,
  info: <Info className="w-4 h-4 text-slate-500" />,
};

const SEVERITY_BORDER: Record<string, string> = {
  critical: 'border-l-red-500',
  warn: 'border-l-amber-500',
  info: 'border-l-slate-300',
};

/** Sağdan kayan bildirim paneli. Okunmamış bildirimleri gösterir,
 *  her birinin yanında "okundu işaretle" + "tümünü işaretle" düğmeleri var.
 */
export default function NotificationPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'unread' | 'all'>('unread');

  async function load() {
    setLoading(true);
    let q = supabase.from('notifications').select('*').order('created_at', { ascending: false }).limit(50);
    if (filter === 'unread') {
      q = q.is('read_at', null);
    }
    const { data } = await q;
    setNotifications((data as Notification[]) ?? []);
    setLoading(false);
  }

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filter]);

  // ESC ile kapan
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    if (open) {
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }
  }, [open, onClose]);

  // Realtime — panel açıkken yeni notification gelirse anında listeye ekle
  useEffect(() => {
    if (!open) return;
    const channel = supabase
      .channel('notifications-panel')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload) => {
          const n = payload.new as Notification;
          if (filter === 'unread' && n.read_at) return;
          setNotifications((prev) => [n, ...prev].slice(0, 50));
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [open, filter]);

  async function markRead(id: number) {
    await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('id', id);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
  }

  async function markAllRead() {
    const unreadIds = notifications.filter((n) => !n.read_at).map((n) => n.id);
    if (unreadIds.length === 0) return;
    await supabase.from('notifications').update({ read_at: new Date().toISOString() }).in('id', unreadIds);
    await load();
  }

  if (!open) return null;

  const visible = filter === 'unread' ? notifications.filter((n) => !n.read_at) : notifications;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside className="w-full sm:w-[420px] bg-white shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Bildirimler</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
          <button
            onClick={() => setFilter('unread')}
            className={`text-xs px-2.5 py-1 rounded-full transition ${filter === 'unread' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}
          >
            Okunmamış
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`text-xs px-2.5 py-1 rounded-full transition ${filter === 'all' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}
          >
            Tümü
          </button>
          <button
            onClick={markAllRead}
            className="ml-auto text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            Tümünü işaretle
          </button>
        </div>

        <div className="divide-y divide-slate-100">
          {loading && <div className="p-6 text-sm text-slate-500">Yükleniyor…</div>}
          {!loading && visible.length === 0 && (
            <div className="p-6 text-sm text-slate-500 text-center">
              {filter === 'unread' ? 'Tüm bildirimler okundu ✓' : 'Henüz bildirim yok'}
            </div>
          )}
          {visible.map((n) => (
            <div key={n.id} className={`pl-3 pr-4 py-3 border-l-4 ${SEVERITY_BORDER[n.severity]} ${n.read_at ? 'opacity-60' : ''}`}>
              <div className="flex items-start gap-2">
                <div className="pt-0.5">{SEVERITY_ICON[n.severity]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium text-slate-900 truncate">{n.title}</h3>
                    {!n.read_at && (
                      <button
                        onClick={() => markRead(n.id)}
                        className="text-slate-400 hover:text-slate-700 flex-shrink-0"
                        title="Okundu olarak işaretle"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <pre className="text-xs text-slate-600 mt-1 whitespace-pre-wrap font-sans">{n.message}</pre>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-slate-400">
                      {new Date(n.created_at).toLocaleString('tr-TR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span className="text-[10px] text-slate-400">·</span>
                    <span className="text-[10px] text-slate-400">{n.type}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
